import React, { useEffect, useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dynamicWebFormPreviewDataAtom,
  entityPreviewDataAtom,
  flowObjAtom,
  flowValidationAtom,
} from '../../../../Services/_states';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import ModalForms from '../../../Shared/Modals/index.js';
import EntityDropdown from '../../../Shared/EntityDropdown/EntityDropdown.tsx';
import CommonBadges from '../../../Shared/Badges/index.jsx';
import { EntityActions } from '../../../../Services/_actions/Entity/entity.actions.ts';
import { getAllParentNodeResults } from '../../helper.tsx';
import CampaignAssignedDropdown from '../../../Shared/CampaignAssignmentDropdown/index.tsx';
import { useSnackBar } from '../../Common/SnackBar/index.tsx';
import { LeadFormActions } from '../../../../Services/_actions/index.js';
import {
  staticColumnsMatch,
} from '../../../Leads/DynamicFormFields/helper.ts';

export type Operator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'is_empty'
  | 'is_not_empty';

export type ValueType = 'string' | 'number' | 'boolean' | 'date';
export type LogicalOperator = 'AND' | 'OR';

export interface ConditionRule {
  id: string;
  left: string; // literal or {{var}}
  operator: Operator;
  // right?: string; // optional for unary ops
  valueType: ValueType;
  // New helpers for string ops
  caseInsensitive?: boolean;
  trim?: boolean;
  entity?: { _id: string; name: string };
  matched_with?: { name: string; label: string };
  entityFormFields?: any[];
  table?: any;
  custom_value?: string;
  campaign?: { proposal_id: string; name: string };
  webForm?: { leads_form_id: number | string; leads_form_name: string };
}

export interface LogicalGroup {
  id: string;
  operator: LogicalOperator;
  type: 'condition' | 'group';
  conditions?: ConditionRule[];
  subGroups?: LogicalGroup[];
}

export interface ConditionalNodeData {
  id: string;
  header?: string;
  ifGroup?: LogicalGroup;
  elseIfGroups?: LogicalGroup[];
  onChange: (id: string, patch: Partial<ConditionalNodeData>) => void;
  onOutputClick?: (id: string) => void;
}
const stringOps: Operator[] = ['equals', 'not_equals', 'contains', 'starts_with', 'ends_with'];
const numericOps: Operator[] = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte'];

const operatorLabel: Record<Operator, string> = {
  equals: 'Equals',
  not_equals: 'Not equals',
  contains: 'Contains',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  gt: '>',
  gte: '\u2265',
  lt: '<',
  lte: '\u2264',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty',
};
const matchFieldTypeOptions = [
  { label: 'Static', value: 'static' },
  { label: 'Dynamic', value: 'dynamic' },
];
const staticFieldOptions = [
  { alias_name: 'Phone Number', key_name: 'phone_number', matched_field_type: 'static' },
  // { alias_name: 'Status', key_name: 'last_status' }
];

const defaultRule = (suffix: string | number): ConditionRule => ({
  id: `rule-${suffix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  left: '',
  operator: 'equals',
  // right: '',
  valueType: 'string',
  caseInsensitive: true,
  trim: true,
});

const defaultGroup = (
  suffix: string | number,
  operator: LogicalOperator = 'AND'
): LogicalGroup => ({
  // id: `group-${suffix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  id: `${suffix}`,
  operator,
  type: 'group',
  conditions: [defaultRule(suffix)],
  subGroups: [],
});

const valueTypeOptions = [
  { label: 'String', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Date (ISO)', value: 'date' },
];

const getOperatorOptions = (valueType: ValueType) => {
  const valueOps = valueType === 'number' ? numericOps : stringOps;
  const availableOps: Operator[] = [...valueOps, 'is_empty', 'is_not_empty'];
  return availableOps.map((op) => ({ label: operatorLabel[op], value: op }));
};

const extractEntitiesFromGroup = (group) => {
  let entities = [];
  try {
    if (group?.conditions?.length) {
      group.conditions.forEach((item) => {
        if (item?.entity?._id) {
          entities.push(item.entity);
        }
      });
    }
    if (group?.subGroups?.length) {
      group.subGroups.forEach((subGroup) => {
        entities = [...entities, ...extractEntitiesFromGroup(subGroup)];
      });
    }
  } catch (error) {
    console.log(error);
  } finally {
    return entities;
  }
};

const extractAllEntities = (data) => {
  let entities = [];
  try {
    if (data?.ifGroup) {
      entities = [...entities, ...extractEntitiesFromGroup(data.ifGroup)];
    }
    if (data?.elseIfGroups?.length) {
      data.elseIfGroups.forEach((group) => {
        entities = [...entities, ...extractEntitiesFromGroup(group)];
      });
    }
  } catch (error) {
    console.log(error);
  } finally {
    return entities;
  }
};

const ConditionalNode: React.FC<{ data: ConditionalNodeData } & any> = ({ data }) => {
  const validation = useRecoilValue(flowValidationAtom);
  const flowObj = useRecoilValue(flowObjAtom);
  const [entityPreviewData, setEntityPreviewData] = useRecoilState(entityPreviewDataAtom);
  // const parentResults = data?.parentResults ?? [];
  const parentResults = getAllParentNodeResults(data?.id ?? null, flowObj);
  const [webFormList, setWebFormList] = useRecoilState(dynamicWebFormPreviewDataAtom);
  const entityActions = EntityActions();

  const getEntityFormById = async (entity) => {
    try {
      if (!entity?._id) return;
      if (entityPreviewData?.[entity?._id]) return;
      const response = await entityActions?.getFormById(entity?._id);
      if (response?.status === 200 && response?.data?.items) {
        const form = response?.data?.items;
        // setEntityPreviewData({ ...entityPreviewData, [entity?._id]: form });
        setEntityPreviewData((prev) => ({
          ...prev,
          [entity._id]: form,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };
  const { showSnackBar } = useSnackBar();
  const [formLoading, setFormLoading] = useState(false);
  const actions = LeadFormActions();

  const [open, setOpen] = useState(false);
  const [draftIfGroup, setDraftIfGroup] = useState<LogicalGroup>(
    () => data.ifGroup || defaultGroup(`${data.id}`)
  );
  const [draftElseIfGroups, setDraftElseIfGroups] = useState<LogicalGroup[]>(
    () => data.elseIfGroups || []
  );

  const openModal = () => {
    setDraftIfGroup(data.ifGroup || defaultGroup(`${data.id}`));
    setDraftElseIfGroups(data.elseIfGroups || []);
    setOpen(true);
  };
  const closeModal = () => setOpen(false);

  const saveModal = () => {
    data.onChange(data.id, {
      ifGroup: draftIfGroup,
      elseIfGroups: draftElseIfGroups || [],
    });
    closeModal();
  };

  // Helper to count total conditions in a group recursively
  const countConditions = (group: LogicalGroup): number => {
    let count = (group.conditions || []).length;
    (group.subGroups || []).forEach((subGroup) => {
      count += countConditions(subGroup);
    });
    return count;
  };

  // Helper to generate readable summary of a group
  const getGroupSummary = (group: LogicalGroup): string => {
    const conditionCount = countConditions(group);
    const subGroupCount = (group.subGroups || []).length;

    if (subGroupCount === 0) {
      return `${conditionCount} condition${conditionCount !== 1 ? 's' : ''} (${group.operator})`;
    }
    return `${conditionCount} condition${conditionCount !== 1 ? 's' : ''}, ${subGroupCount} subgroup${subGroupCount !== 1 ? 's' : ''} (${group.operator})`;
  };

  const fetchAllEntityForms = async (conditionalDataObj) => {
    try {
      const entities = extractAllEntities(conditionalDataObj);
      const uniqueEntities = Array.from(new Map(entities.map((e) => [e._id, e])).values());
      for (const entity of uniqueEntities) {
        await getEntityFormById(entity);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ✅ 1️⃣ Extract all campaigns recursively from a group
  const extractCampaignsFromGroup = (
    group: LogicalGroup
  ): { proposal_id: string; name: string }[] => {
    const campaigns: { proposal_id: string; name: string }[] = [];

    if (group?.conditions?.length) {
      group.conditions.forEach((cond) => {
        if (cond?.campaign?.proposal_id) {
          campaigns.push(cond.campaign);
        }
      });
    }

    if (group?.subGroups?.length) {
      group.subGroups.forEach((sub) => {
        campaigns.push(...extractCampaignsFromGroup(sub));
      });
    }
    return campaigns;
  };

  // ✅ 2️⃣ Deduplicate campaigns by proposal_id
  const getUniqueCampaigns = (campaigns) => {
    return Array.from(new Map(campaigns.map((c) => [c.proposal_id, c])).values());
  };

  // ✅ 3️⃣ Fetch all campaign forms
  const fetchAllCampaignForms = async (conditionalDataObj) => {
    try {
      let allCampaigns = [];

      if (conditionalDataObj?.ifGroup)
        allCampaigns.push(...extractCampaignsFromGroup(conditionalDataObj.ifGroup));

      if (conditionalDataObj?.elseIfGroups?.length)
        conditionalDataObj.elseIfGroups.forEach((g) =>
          allCampaigns.push(...extractCampaignsFromGroup(g))
        );

      const uniqueCampaigns = getUniqueCampaigns(allCampaigns);

      for (const campaign of uniqueCampaigns) {
        await getWebFormListByCampaign(campaign);
      }
    } catch (error) {
      console.warn('fetchAllCampaignForms error', error);
    }
  };

  useEffect(() => {
    fetchAllCampaignForms(data);
    fetchAllEntityForms(data);
  }, [data]);

  const getWebFormListByCampaign = async (campaign: any, forceRefresh = false) => {
    try {
      if (!campaign?.proposal_id) return null;

      if (!forceRefresh && webFormList?.[campaign.proposal_id]) {
        return webFormList[campaign.proposal_id];
      }

      const response = await actions.getFormsByCampaignId(campaign.proposal_id);
      if (response?.status === 200) {
        const forms = response?.data ?? null;

        // merge into atom
        setWebFormList((prev: any) => ({
          ...(prev || {}),
          [campaign.proposal_id]: forms,
        }));

        return forms;
      }
    } catch (error) {
      console.warn('getWebFormListByCampaign error', error);
    }
    return null;
  };

  const handleSelectCampaign = async (campaign: any) => {
    if (!campaign?.proposal_id) {
      showSnackBar('Please select a campaign first', 'warning');
      return;
    }

    try {
      setFormLoading(true);
      const leadForms = await getWebFormListByCampaign(campaign);

      if (!leadForms) {
        showSnackBar('No forms found for selected campaign', 'info');
      }
    } catch (error) {
      showSnackBar('Failed to fetch forms', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const modifyWebFormListObj = (obj: any) => {
    try {
      if (!obj || Object.keys(obj).length === 0) {
        return [];
      }
      return Object.values(obj);
    } catch (error) {
      console.warn(error);
      return [];
    }
  };
  const getWebFormFieldsById = (campaign, webForm) => {
    try {
      const forms = webFormList?.[campaign?.proposal_id];
      const fields = forms?.[webForm?.leads_form_id];
      // const formItems = Object.values(fields?.leads_form_items || [])?.map((item: any) => ({
      //   key_name: item?.key_name,
      //   alias_name: item?.alias_name,
      //   ...(item?.statusOption && { statusOption: item?.statusOption }),
      // }));
      // const formItems = !staticColumnsMatch.Object.values(fields?.leads_form_items || []);
      const formItems = staticColumnsMatch(Object.values(fields?.leads_form_items || []));
      return formItems ?? [];
    } catch (error) {
      console.warn(error);
      return [];
    }
  };
  const [statusCache, setStatusCache] = useState<Record<string, any>>({});
  useEffect(() => {
    const allGroups = [draftIfGroup, ...draftElseIfGroups];
    allGroups.forEach((group) => {
      (group?.conditions || [])?.forEach((item) => {
        const formId = item?.webForm?.leads_form_id;
        if (formId && !statusCache[formId]) {
          actions.fetchStatusAndSubStatus({ leadFormId: formId, active: true }).then((res) => {
            if (res?.status === 200) {
              const allStatuses = res?.data || [];
              const statusData: Record<string, any[]> = {};
              allStatuses.forEach((item) => {
                if (item?.type_of_status) {
                  if (!statusData[item.type_of_status]) statusData[item.type_of_status] = [];
                  statusData[item.type_of_status].push(item);
                }
                item?.substatuses?.forEach((sub) => {
                  const key = sub?.type_of_status || 'unknown';
                  if (!statusData[key]) statusData[key] = [];
                  statusData[key].push(sub);
                });
              });
              setStatusCache((prev) => ({ ...prev, [formId]: statusData }));
            }
          });
        }
      });
    });
  }, [draftIfGroup, draftElseIfGroups]);

  // ------- Rule Editor Component -------
  const renderRuleEditor = (
    rule: ConditionRule,
    setRule: (patch: Partial<ConditionRule>) => void
  ) => {
    const opOpts = getOperatorOptions(rule?.valueType);
    const showStringToggles = rule.valueType === 'string';
    const webFormOptions = modifyWebFormListObj(webFormList?.[rule?.campaign?.proposal_id]);
    const webFormFields = getWebFormFieldsById(rule?.campaign, rule?.webForm);
    const statusData = rule?.webForm?.leads_form_id
      ? statusCache[rule.webForm.leads_form_id] || {}
      : {};
    const currentMatchFieldType = webFormFields?.find(
      (item) => item?.key_name === rule?.matched_with?.key_name
    );

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <SearchableDropdown
            options={[...parentResults, { label: 'Others', value: 'others' }]}
            value={
              [...parentResults, { label: 'Others', value: 'others' }]?.find(
                (item) => item?.value === rule?.left
              ) ?? null
            }
            onChange={(e) => setRule({ left: e?.value ?? '' })}
            label={'Select Parameter'}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SearchableDropdown
            options={opOpts}
            value={rule?.operator}
            onChange={(e) =>
              setRule({
                operator: e?.value ?? '',
              })
            }
            // onChange={(value) => setRule({ operator: value as Operator })}
            value={opOpts?.find((item) => item?.value === rule?.operator) ?? null}
            label="Operator"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <SearchableDropdown
            options={[
              { label: 'Entity', value: 'entity' },
              { label: 'Lead', value: 'lead' },
              // { label: 'Custom', value: 'custom' },
            ]}
            value={
              [
                { label: 'Entity', value: 'entity' },
                { label: 'Lead', value: 'lead' },
                // { label: 'Custom', value: 'custom' },
              ]?.find((item) => item?.value === rule?.table) ?? null
            }
            onChange={(value) =>
              setRule({
                table: value?.value ?? ('' as Operator),
                entity: null,
                matched_with: null,
                campaign: null,
                webForm: null,
                custom_value: '',
              })
            }
            label="Table"
          />
        </Grid>
        {/* {rule?.table?.value === 'custom' ? (
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              value={rule?.custom_value}
              placeholder="Custom value"
              onChange={(e) => setRule({ custom_value: e.target.value })}
            />
          </Grid>
        ) :  */}

        {rule?.table === 'lead' ? (
          <>
            {/* Lead dropdowns */}
            <Grid item xs={12} md={3} sx={{ mt: 0 }}>
              <CampaignAssignedDropdown
                label={'Select Campaign'}
                placeholder="Select Campaign"
                value={rule?.campaign ?? null}
                autoSelectValue={true}
                onChange={(value) => {
                  if (value && value.proposal_id) {
                    const campaignObj = { proposal_id: value.proposal_id, name: value.name };
                    setRule({
                      campaign: campaignObj,
                      table: 'lead',
                      webForm: null,
                    });
                    handleSelectCampaign(campaignObj);
                  } else {
                    setRule({
                      campaign: null,
                      webForm: null,
                    });
                  }
                }}
                sx={{}}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SearchableDropdown
                label={
                  <Typography component="span">
                    Select Form <span style={{ color: 'red' }}>*</span>
                  </Typography>
                }
                loading={formLoading}
                options={webFormOptions}
                value={
                  (webFormOptions ?? [])?.find(
                    (form) => form?.leads_form_id === rule?.webForm?.leads_form_id
                  ) ?? null
                }
                labelKey="leads_form_name"
                valueKey="leads_form_id"
                onChange={(selected) => {
                  setRule({
                    webForm: selected
                      ? {
                          leads_form_id: selected.leads_form_id,
                          leads_form_name: selected.leads_form_name,
                        }
                      : null,
                  });
                }}
                onOpen={async () => {
                  if (rule?.campaign) {
                    await getWebFormListByCampaign(rule.campaign, false);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SearchableDropdown
                options={[...staticFieldOptions, ...webFormFields]}
                value={
                  [...staticFieldOptions, ...webFormFields]?.find(
                    (item) => item?.key_name === rule?.matched_with?.key_name
                  ) ?? null
                }
                labelKey="alias_name"
                valueKey="key_name"
                onChange={(e) => {
                  setRule({
                    matched_with: { key_name: e?.key_name },
                    matched_field_type: e?.matched_field_type ?? 'dynamic',
                  });
                }}
                label={
                  <Typography>
                    Select Form Field <span style={{ color: 'red' }}>*</span>
                  </Typography>
                }
              />
            </Grid>
            {rule?.left === 'others' && (
              <Grid item xs={12} md={3}>
                {(() => {
                  const { key_type, statusOption, key_options } = currentMatchFieldType || {};

                  switch (key_type) {
                    case 'TEXT':
                      return (
                        <TextField
                          size="small"
                          label="Enter Value"
                          fullWidth
                          value={rule?.custom_value ?? ''}
                          onChange={(e) =>
                            setRule({
                              ...rule,
                              custom_value: e.target.value ?? '',
                            })
                          }
                        />
                      );

                    case 'DROPDOWN':
                      if (statusOption === 'is_status') {
                        const keyName = String(rule?.matched_with?.key_name);
                        const options = statusData?.[keyName] ?? [];

                        return (
                          <SearchableDropdown
                            label="Select Status"
                            options={options}
                            labelKey="status_name"
                            valueKey="status_id"
                            value={options.find(
                              (item) => item?.status_id === rule?.param?.status_id
                            )}
                            onChange={(value) =>
                              setRule({
                                ...rule,
                                param: { status_id: value?.status_id ?? '' },
                              })
                            }
                          />
                        );
                      }
                      if (statusOption === 'is_sub_status') {
                        const keyName = String(rule?.matched_with?.key_name);
                        const options = statusData?.[keyName] ?? [];

                        return (
                          <SearchableDropdown
                            label="Select Sub Status"
                            options={options}
                            labelKey="name"
                            valueKey="sub_status_id"
                            value={options.find(
                              (item) => item?.sub_status_id === rule?.param?.sub_status_id
                            )}
                            onChange={(value) =>
                              setRule({
                                ...rule,
                                param: { sub_status_id: value?.sub_status_id ?? '' },
                              })
                            }
                          />
                        );
                      }

                      return (
                        <SearchableDropdown
                          label="Select Value"
                          options={
                            key_options?.map((opt) => ({
                              label: opt,
                              value: opt,
                            })) ?? []
                          }
                          value={
                            rule?.param?.value
                              ? { label: rule?.param?.value, value: rule?.param?.value }
                              : null
                          }
                          onChange={(value) =>
                            setRule({
                              ...rule,
                              param: { value: value?.value ?? '' },
                            })
                          }
                        />
                      );

                    // 🟡 Future: add more types here easily
                    // case "NUMBER": ...
                    // case "DATE": ...

                    default:
                      return (
                        <TextField
                          size="small"
                          label="Enter Value"
                          fullWidth
                          value={rule?.custom_value ?? ''}
                          onChange={(e) =>
                            setRule({
                              ...rule,
                              custom_value: e.target.value ?? '',
                            })
                          }
                        />
                      );
                  }
                })()}
              </Grid>
            )}
          </>
        ) : (
          <>
            <Grid item xs={12} md={3} sx={{ mt: -2 }}>
              <EntityDropdown
                uniqueId={`${data?.id}-${rule.id}`}
                value={rule?.entity ?? null}
                onChange={(value) => {
                  if (value) {
                    setRule({ entity: { _id: value?._id, name: value?.name }, matched_with: null });
                    getEntityFormById(value);
                    // setEntityPreviewData({ ...entityPreviewData, [value?._id]: value });
                  } else {
                    setRule({ entity: value, matched_with: null });
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <SearchableDropdown
                options={entityPreviewData?.[rule?.entity?._id]?.form_fields ?? []}
                value={
                  entityPreviewData?.[rule?.entity?._id]?.form_fields?.find(
                    (item) => rule?.matched_with?.name === item?.name
                  ) ?? []
                }
                onChange={(value) => {
                  if (value) {
                    setRule({
                      matched_with: { name: value?.name, label: value?.label },
                    });
                  } else {
                    setRule({ matched_with: value });
                  }
                }}
                label="Entity Form Field"
              />
            </Grid>
          </>
        )}
        {showStringToggles && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              String options
            </Typography>
            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!rule.caseInsensitive}
                    onChange={(e) => setRule({ caseInsensitive: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Case-insensitive</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!rule.trim}
                    onChange={(e) => setRule({ trim: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Trim</Typography>}
              />
            </Stack>
          </Grid>
        )}
      </Grid>
    );
  };
  const ROW_H = 28;
  const ROW_GAP = 4;
  const SUMMARY_TOP_PAD = 0;
  const HANDLE_Y_OFFSET = 3;
  const HANDLE_SIZE = 12;
  const HANDLE_X_OFFSET = -12;
  // ------- Group Editor Component -------
  const renderLogicalGroup = (
    group: LogicalGroup,
    setGroup: (group: LogicalGroup) => void,
    groupLabel: string,
    groupColor: 'primary' | 'secondary' = 'primary',
    canDelete = false,
    onDelete?: () => void,
    nestLevel = 0
  ): React.ReactNode => {
    const conditions = group.conditions || [];
    const subGroups = group.subGroups || [];

    return (
      <Card
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          ml: nestLevel * 2,
          backgroundColor:
            nestLevel === 0
              ? groupColor === 'primary'
                ? 'rgba(25, 118, 210, 0.04)'
                : 'rgba(156, 39, 176, 0.04)'
              : 'rgba(0, 0, 0, 0.02)',
          borderLeft: nestLevel > 0 ? '3px solid #e0e0e0' : undefined,
        }}
      >
        {/* Group Header */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CommonBadges value={groupLabel} color={groupColor} />

          {/* Logical Operator Selector */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={group.operator}
              onChange={(e) => setGroup({ ...group, operator: e.target.value as LogicalOperator })}
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="AND">AND</MenuItem>
              <MenuItem value="OR">OR</MenuItem>
            </Select>
          </FormControl>

          <Tooltip
            title={`All ${group.operator === 'AND' ? 'conditions must be true' : 'at least one condition must be true'}`}
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          {canDelete && onDelete && (
            <IconButton size="small" onClick={onDelete} aria-label={`Remove ${groupLabel}`}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Render Conditions */}
        {conditions.map((rule, conditionIndex) => (
          <Box key={rule.id}>
            {(conditionIndex > 0 || subGroups.length > 0) && (
              <Stack direction="row" alignItems="center" sx={{ my: 1 }}>
                <Divider sx={{ flex: 1 }} />
                <CommonBadges
                  size="small"
                  value={group?.operator}
                  color={group.operator === 'AND' ? 'primary' : 'warning'}
                  // sx={{
                  //   mx: 2,
                  //   fontSize: '0.7rem',
                  //   fontWeight: 'bold',
                  //   bgcolor: group.operator === 'AND' ? 'success.light' : 'warning.light',
                  //   color: 'white',
                  // }}
                />
                <Divider sx={{ flex: 1 }} />
              </Stack>
            )}

            <Card variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.paper' }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  {renderRuleEditor(rule, (patch) => {
                    const nextConditions = conditions.slice();
                    nextConditions[conditionIndex] = {
                      ...nextConditions[conditionIndex],
                      ...patch,
                    };
                    setGroup({ ...group, conditions: nextConditions });
                  })}
                </Box>
                {conditions?.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const nextConditions = conditions.slice();
                      nextConditions.splice(conditionIndex, 1);
                      setGroup({ ...group, conditions: nextConditions });
                    }}
                    aria-label="Remove condition"
                    sx={{ mt: 1 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Card>
          </Box>
        ))}

        {/* Render Sub-groups */}
        {subGroups?.map((subGroup, subGroupIndex) => (
          <Box key={subGroup.id}>
            {(conditions.length > 0 || subGroupIndex > 0) && (
              <Stack direction="row" alignItems="center" sx={{ my: 1 }}>
                <Divider sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  label={group.operator}
                  sx={{
                    mx: 2,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    bgcolor: group.operator === 'AND' ? 'success.light' : 'warning.light',
                    color: 'white',
                  }}
                />
                <Divider sx={{ flex: 1 }} />
              </Stack>
            )}

            {renderLogicalGroup(
              subGroup,
              (updatedSubGroup) => {
                const nextSubGroups = subGroups.slice();
                nextSubGroups[subGroupIndex] = updatedSubGroup;
                setGroup({ ...group, subGroups: nextSubGroups });
              },
              `Group ${subGroupIndex + 1}`,
              'secondary',
              true,
              () => {
                const nextSubGroups = subGroups.slice();
                nextSubGroups.splice(subGroupIndex, 1);
                setGroup({ ...group, subGroups: nextSubGroups });
              },
              nestLevel + 1
            )}
          </Box>
        ))}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            onClick={() => {
              const newRule = defaultRule(
                `${groupLabel.toLowerCase().replace(/\s+/g, '-')}-${conditions.length}`
              );
              setGroup({ ...group, conditions: [...conditions, newRule] });
            }}
            size="small"
            startIcon={<AddCircleOutlineIcon fontSize="small" />}
            variant="outlined"
          >
            Add Condition
          </Button>

          <Button
            onClick={() => {
              const newSubGroup = defaultGroup(
                `${data.id}-subgroup-${subGroups.length}`,
                group.operator === 'AND' ? 'OR' : 'AND'
              );
              setGroup({ ...group, subGroups: [...subGroups, newSubGroup] });
            }}
            size="small"
            startIcon={<AccountTreeIcon fontSize="small" />}
            variant="outlined"
            color="secondary"
          >
            Add Sub-group
          </Button>
        </Stack>
      </Card>
    );
  };

  const modalBody = (
    <Box>
      {/* IF Group Editor */}
      {renderLogicalGroup(draftIfGroup, setDraftIfGroup, 'IF', 'primary')}

      {/* ELSE IF Groups */}
      {draftElseIfGroups.map((group, groupIndex) =>
        renderLogicalGroup(
          group,
          (updatedGroup) => {
            const nextGroups = draftElseIfGroups.slice();
            nextGroups[groupIndex] = updatedGroup;
            setDraftElseIfGroups(nextGroups);
          },
          `ELSE IF ${groupIndex + 1}`,
          'secondary',
          true,
          () => {
            const nextGroups = draftElseIfGroups.slice();
            nextGroups.splice(groupIndex, 1);
            setDraftElseIfGroups(nextGroups);
          }
        )
      )}

      <Button
        onClick={() => {
          const newGroup = defaultGroup(`${data.id}-elseif-${draftElseIfGroups.length + 1}`);
          setDraftElseIfGroups([...draftElseIfGroups, newGroup]);
        }}
        size="small"
        startIcon={<AddCircleOutlineIcon fontSize="small" />}
        variant="contained"
        color="secondary"
        sx={{ mb: 2 }}
      >
        Add ELSE IF Group
      </Button>

      <Divider sx={{ my: 2 }} />

      {/* ELSE info */}
      <Box
        sx={{
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.default',
        }}
      >
        <CommonBadges value="ELSE" color="default" />
        <Typography variant="caption" sx={{ ml: 1 }}>
          Fallback branch when none of the above condition groups match.
        </Typography>
      </Box>
    </Box>
  );

  const modalFooter = (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button variant="outlined" size="small" onClick={closeModal}>
        Cancel
      </Button>
      <Button variant="contained" size="small" onClick={saveModal}>
        Save
      </Button>
    </Stack>
  );

  // -------- Node compact view --------
  return (
    <Box
      sx={{
        background: 'transparent',
        border: 'none',
        p: 1,
        position: 'relative',
        overflow: 'visible',
        zIndex: 1,
        width: 350,
        maxWidth: '100%',
      }}
    >
      {/* <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {data.header || 'Conditional'}
      </Typography> */}

      <Stack spacing={ROW_GAP / 8} sx={{ mb: 1, position: 'relative', pr: 5 }}>
        {/* IF Group Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: ROW_H }}>
          <CommonBadges value="IF" color="primary" />
          {data.ifGroup && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              ({getGroupSummary(data?.ifGroup)})
            </Typography>
          )}
        </Box>

        {/* ELSE IF Groups Summary */}
        {(data.elseIfGroups || []).map((group, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, height: ROW_H }}>
            <CommonBadges value={`ELSE IF ${i + 1}`} color="secondary" />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              ({getGroupSummary(group)})
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: ROW_H }}>
          <CommonBadges value="ELSE" color="default" />
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={openModal}
          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
        >
          Edit Conditions
        </Button>
      </Stack>

      {/* Validation (if any) */}
      {validation?.[data.id]?.errorMsg && (
        <Typography color="error" variant="caption">
          {validation[data.id].errorMsg}
        </Typography>
      )}

      {/* OUTPUT HANDLES */}
      {(() => {
        const labels = [
          'IF',
          ...(data.elseIfGroups || []).map((_, i) => `ELSE IF ${i + 1}`),
          'ELSE',
        ];

        return labels.map((label, idx) => {
          const id =
            label === 'IF'
              ? `${data.id}`
              : label.startsWith('ELSE IF')
                ? `${data.id}-elseif-${label.split(' ')[2]}`
                : `${data.id}-else`;

          const color = label === 'ELSE' ? '#6d4c41' : '#2e7d32';

          const gapPx = ROW_GAP; // Stack spacing in px; tune if your theme differs
          const topPx = SUMMARY_TOP_PAD + idx * (ROW_H + gapPx) + ROW_H / 2 + HANDLE_Y_OFFSET;

          return (
            <React.Fragment key={id}>
              <Handle
                type="source"
                position={Position.Right}
                id={id}
                style={{
                  position: 'absolute',
                  top: topPx,
                  background: color,
                  width: 10,
                  height: 10,
                  right: -16,
                  transform: 'translateY(-50%)',
                }}
                onClick={() => data.onOutputClick?.(data.id)}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: 'calc(100% + 15px)',
                  top: topPx,
                  transform: 'translateY(-50%)',
                  color,
                  fontWeight: 700,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </React.Fragment>
          );
        });
      })()}

      {/* MODAL */}
      <ModalForms
        show={open}
        handleClose={closeModal}
        modalHeader={data.header || 'Configure Conditions'}
        modalBody={modalBody}
        modalFooter={modalFooter}
        modalId={`${data.id}-conditions`}
        size="lg"
      />
    </Box>
  );
};

export default ConditionalNode;
